import { DragOverlay, defaultDropAnimation } from "@dnd-kit/core";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { Box, Skeleton, Typography } from "@mui/material";
import type { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import React, { type FC, Fragment, type MouseEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import type { Department } from "../../../../contexts/AuthProvider";
import { useAnalytics } from "../../../../contexts/analytics/hooks";
import { formatWeekRangeWithMonth } from "../../../../utils/dateFormatting";
import type { DepartmentLocation, Nurse, NurseRole, RecurringShift, WeeklyRecurring } from "../../../../utils/models";
import type { SortingState } from "../../../../utils/multiColumnSorting";
import { SortableHeaderButton } from "../auxillary/SortableHeaderButton";
import { ClickableShiftCell } from "../Cells/ClickableShiftCell";
import type { DraggableShiftData } from "../Cells/DraggableShift";
import { LocationCell } from "../Cells/LocationCell";
import { LocationCellProvider } from "../Cells/LocationCellContext";
import type { WeeksAndActiveState } from "../useGetMasterScheduleWeeksAndActiveState";
import {
	amountOfLeadingColumns,
	DEFAULT_VISIBLE_LOCATIONS,
	getGridSx,
	getSectionHeaderStyle,
	getWeeksHeaderCardStyle,
	gridColumnHeadersStyle,
	leadingColumnsPlaceholderStyle,
	masterScheduleSortingOptions,
	type ScheduleData,
	type Section,
	type SectionLocationMapReadOnly,
	sectionHoverShowAllStyle,
	showAllBtnClassName,
	showAllBtnStyle,
	showAllFooterStyle,
	weekHeaderCardStyle,
} from "../utils";
import { DraggableSectionHeader } from "./DraggableSection";
import { GridDivider } from "./GridDivider";
import { MasterScheduleRow } from "./MasterScheduleRow";
import { SectionDropZone } from "./SectionDropZone";

type Props = {
	amountOfWeeks: number;
	filtersBoxElement: HTMLDivElement | null;
	weeksAndActiveState: WeeksAndActiveState[];
	handleSortHeaderClicked: (e: MouseEvent, optionIndex: number) => void;
	masterScheduleSortState: Array<SortingState<DepartmentLocation>>;
	departmentSpecificRoles: NurseRole[];
	refetchRecurringShifts: (options?: RefetchOptions) => Promise<QueryObserverResult<RecurringShift[], Error>>;
	/** Retries whichever load put the grid in its error state. */
	retryLoad: () => void;
	uniqueSections: Section[];
	expandedGroups: Set<string>;
	firstDateOfCurrentWeek: DateTime;
	calculatedData: SectionLocationMapReadOnly | null;
	isCalculating: boolean;
	loadError: boolean;
	chosenDepartment: Department;
	handleSectionCollapseToggle: (section: Section) => void;
	recurringDataInWeeks: RecurringShift[] | undefined;
	getNurseById: (id: number) => Nurse | undefined;
	activeDragId: string | null;
	activeDragData: DraggableShiftData | null;
	onShiftClick: (recurringShift: RecurringShift, event: MouseEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	onShiftKeyDown: (recurringShift: RecurringShift, event: React.KeyboardEvent<HTMLElement>, clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	handleOnAddClick: (clickedDate: DateTime, clickedLocation: DepartmentLocation) => void;
	onCtrlPressedChange?: (isCtrlPressed: boolean) => void;
	isSynergyMS?: boolean;
	sectionOrder: Section[];
	isDraggingSection: boolean;
	showAllLocationsSections: Set<string>;
	toggleShowAllLocations: (section: Section) => void;
	weekStartDay?: number;
};

export const MasterScheduleGrid: FC<Props> = ({
	amountOfWeeks,
	filtersBoxElement,
	weeksAndActiveState,
	handleSortHeaderClicked,
	masterScheduleSortState,
	departmentSpecificRoles,
	refetchRecurringShifts,
	retryLoad,
	uniqueSections,
	firstDateOfCurrentWeek,
	calculatedData,
	isCalculating,
	loadError,
	chosenDepartment,
	expandedGroups,
	handleSectionCollapseToggle,
	recurringDataInWeeks,
	getNurseById,
	activeDragId,
	activeDragData,
	onShiftKeyDown,
	handleOnAddClick,
	onShiftClick,
	onCtrlPressedChange,
	isSynergyMS = false,
	sectionOrder,
	isDraggingSection,
	showAllLocationsSections,
	toggleShowAllLocations,
	weekStartDay,
}) => {
	const { trackError } = useAnalytics();
	const { t } = useTranslation();
	const [didDelayFirstAnimation, setDidDelayFirstAnimation] = useState(true);
	useEffect(() => {
		if (activeDragId) {
			setDidDelayFirstAnimation(false);
			const delayTimer = setTimeout(() => {
				setDidDelayFirstAnimation(true);
			}, 1000);
			return () => clearTimeout(delayTimer);
		}
	}, [activeDragId]);

	const [localCtrlPressed, setLocalCtrlPressed] = useState(false);
	const handleOnCtrlPressedChange = (isCtrlPressed: boolean) => {
		setLocalCtrlPressed(isCtrlPressed);
		if (onCtrlPressedChange) {
			onCtrlPressedChange(isCtrlPressed);
		}
	};

	const orderedSections = React.useMemo(() => {
		// Create ordered sections array
		if (!calculatedData) return [];

		const allSections = Object.keys(calculatedData);

		// Sort sections based on custom order
		return [...allSections].sort((a, b) => {
			const aIndex = sectionOrder.indexOf(a);
			const bIndex = sectionOrder.indexOf(b);

			// Both in custom order - use order
			if (aIndex !== -1 && bIndex !== -1) {
				return aIndex - bIndex;
			}
			// Only a in custom order - a comes first
			if (aIndex !== -1) {
				return -1;
			}
			// Only b in custom order - b comes first
			if (bIndex !== -1) {
				return 1;
			}
			// Neither in custom order - alphabetical
			return a.localeCompare(b);
		});
	}, [calculatedData, sectionOrder]);

	return (
		<LocationCellProvider weekStartDay={weekStartDay}>
			<Box
				id={"master-schedule-v2-grid"}
				data-testid="master-schedule-grid"
				data-annie-target="master-schedule-grid"
				sx={{
					mt: 3,
					...(getGridSx(amountOfWeeks, filtersBoxElement?.offsetHeight ?? 0, isSynergyMS) as object),
				}}
			>
				<DragOverlay dropAnimation={defaultDropAnimation} transition={!didDelayFirstAnimation ? "transform 400ms cubic-bezier(0.22, 1, 0.36, 1)" : undefined}>
					{/*This is used to animate the dropping animation*/}
					{activeDragId && activeDragData ? (
						<div style={{ opacity: 0.8 }}>
							<ClickableShiftCell
								nurseId={activeDragData.nurseId}
								recurringShiftId={activeDragData.recurringShiftId}
								onShiftKeyDown={(_) => {}}
								onShiftClick={(_) => {}}
								relevantNurse={getNurseById(activeDragData.nurseId)}
								recurringShiftStartDate={""}
								multipleShifts={false}
								isFutureShift={false}
								isDraggingSelf={true}
								isDraggingContext={true}
								isCopying={localCtrlPressed}
								isSynergyMS={isSynergyMS}
								isCredentialed={true}
							/>
						</div>
					) : null}
				</DragOverlay>
				<Box sx={leadingColumnsPlaceholderStyle} />
				<Box sx={getWeeksHeaderCardStyle(amountOfWeeks)}>
					{/*Week headers section*/}
					{weeksAndActiveState.map((week, index) => (
						<Fragment key={week.firstDate.toISO()}>
							<Box sx={weekHeaderCardStyle} className="ms2-week-header" data-week-index={index} data-testid={`master-schedule-week-header-${index}`}>
								{t("week")} {week.weekNumber} ({t(week.isActive ? "active" : "upcoming")}{" "}
								{formatWeekRangeWithMonth(week.firstDate, week.firstDate.plus({ days: 6 }))})
							</Box>
							{index < weeksAndActiveState.length - 1 && <GridDivider />}
						</Fragment>
					))}
				</Box>
				{/*Grid columns section*/}
				<LocationCell
					sortableHeaderButtonProps={{
						onSortClick: (e) => handleSortHeaderClicked(e, 1),
						sortOrder: masterScheduleSortState.find((s) => s.sortColumns.every((col) => masterScheduleSortingOptions[1].sortColumns?.includes(col)))?.sortOrder,
					}}
					isLocationOutsideOfConfig={true}
					departmentSpecificRoles={departmentSpecificRoles}
					refreshRecurringShifts={refetchRecurringShifts}
					weeklyShifts={null}
					uniqueSections={uniqueSections}
					isSynergyMS={isSynergyMS}
				/>
				<SortableHeaderButton
					injectStyle={true}
					sortOrder={
						isSynergyMS
							? undefined
							: masterScheduleSortState.find((s) => s.sortColumns.every((col) => masterScheduleSortingOptions[0].sortColumns?.includes(col)))?.sortOrder
					}
					onSortClick={isSynergyMS ? undefined : (e) => handleSortHeaderClicked(e, 0)}
					label={t(isSynergyMS ? "Visits" : "Shift")}
				/>
				{Array.from({ length: amountOfWeeks }, (_, weekIndex) => {
					return Array.from({ length: 7 }, (_, dayIndex) => {
						const colIndex = 2 + weekIndex * 7 + dayIndex; // 2 leading columns
						const exactDate = firstDateOfCurrentWeek?.plus({ days: weekIndex * 7 + dayIndex });
						return (
							<Fragment key={exactDate?.toISO() ?? `${weekIndex}-${dayIndex}`}>
								<Box
									sx={gridColumnHeadersStyle}
									data-col-index={colIndex}
									data-week-index={weekIndex}
									className={"ms2-col-header"}
									data-testid={`master-schedule-col-header-${weekIndex}-${dayIndex}`}
								>
									{exactDate?.toFormat("ccc")}
								</Box>
								{dayIndex === 6 && weekIndex < amountOfWeeks - 1 && <GridDivider />}
							</Fragment>
						);
					});
				})}
				{loadError && !calculatedData ? (
					<Box
						sx={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 6 }}
						data-testid="master-schedule-load-error"
					>
						<Typography color="error">{t("error.general")}</Typography>
						<SystemButton buttonVariant="secondary" onClick={retryLoad}>
							{t("Refresh")}
						</SystemButton>
					</Box>
				) : (
					(!calculatedData ? [""] : orderedSections).map((section, sectionIndex) => {
						if (isCalculating || !calculatedData) {
							return (
								<Fragment key={section}>
									<Box sx={getSectionHeaderStyle(amountOfWeeks)}>{section}</Box>
									{Array.from({ length: (amountOfLeadingColumns + 7 * amountOfWeeks) * Math.round(chosenDepartment.locations.length * 0.3) }, (_, index) => (
										// biome-ignore lint/suspicious/noArrayIndexKey:  We don't have a unique key for these empty cells, so we use index
										<Skeleton key={index} variant={"rounded"} />
									))}
								</Fragment>
							);
						}
						const currentScheduleData = calculatedData[section];
						if (!currentScheduleData) {
							trackError("MasterScheduleV2_noDataForSection", { section });
							return null;
						}
						if (currentScheduleData.length === 0) {
							//this is ok - may happen due to selected role filters
							return null;
						}
						const isCollapsed = !expandedGroups.has(section);
						const showAll = showAllLocationsSections.has(section);
						const totalLocations = currentScheduleData.length;

						const visibleScheduleData = (() => {
							// When showing all or few enough locations, preserve the existing sort order (floorToIndex)
							if (totalLocations <= DEFAULT_VISIBLE_LOCATIONS || showAll) return currentScheduleData;

							// Sort by popularity (weekly recurring shifts × weekdays) to pick the top visible locations
							const popularityScores = new Map<ScheduleData, number>();
							for (const scheduleData of currentScheduleData) {
								const loc = scheduleData.location;
								const matchingShifts =
									recurringDataInWeeks?.filter((rs) => {
										if (rs.recurringParams.recurringType !== "Weekly") return false;
										const relevantNurse = getNurseById(rs.nurseId);
										return (
											(loc.location ? rs.location === loc.location : !rs.location) &&
											loc.shift_start.substring(0, 5) === rs.shiftStart.substring(0, 5) &&
											loc.shift_end.substring(0, 5) === rs.shiftEnd.substring(0, 5) &&
											(!relevantNurse || loc.roles.includes("all") || loc.roles.some((role) => relevantNurse.role === role))
										);
									}) ?? [];
								const score = matchingShifts.reduce((sum, rs) => sum + (rs.recurringParams as WeeklyRecurring).weekdays.length, 0);
								popularityScores.set(scheduleData, score);
							}

							return [...currentScheduleData].sort((a, b) => (popularityScores.get(b) ?? 0) - (popularityScores.get(a) ?? 0));
						})();

						return (
							<Fragment key={section}>
								<SectionDropZone index={sectionIndex} amountOfWeeks={amountOfWeeks} isVisible={isDraggingSection} />
								<Box sx={sectionHoverShowAllStyle} data-testid={`master-schedule-section-${section}`}>
									<DraggableSectionHeader
										section={section}
										amountOfWeeks={amountOfWeeks}
										isSynergyMS={isSynergyMS}
										weeksAndActiveState={weeksAndActiveState}
										isCollapsed={isCollapsed}
										handleSectionCollapseToggle={handleSectionCollapseToggle}
										totalLocations={totalLocations}
										visibleLocations={isCollapsed ? 0 : visibleScheduleData.length}
									/>

									{visibleScheduleData.map((scheduleData, scheduleIndex) => {
										const currentLocation = scheduleData.location;
										const currentWeeklyShifts = scheduleData.weeklyShifts;
										const locationRecurringShifts = recurringDataInWeeks?.filter((recurringShift) => {
											const relevantNurse = getNurseById(recurringShift.nurseId);
											return (
												(currentLocation.location ? recurringShift.location === currentLocation.location : !recurringShift.location) &&
												currentLocation.shift_start.substring(0, 5) === recurringShift.shiftStart.substring(0, 5) &&
												currentLocation.shift_end.substring(0, 5) === recurringShift.shiftEnd.substring(0, 5) &&
												(!relevantNurse || currentLocation.roles.includes("all") || currentLocation.roles.some((role) => relevantNurse.role === role))
											);
										});
										if (
											(!currentLocation.location || currentLocation.location.trim().length === 0) &&
											(!locationRecurringShifts || locationRecurringShifts.length === 0) &&
											scheduleData.isLocationOutsideOfConfig
										) {
											// don't render "No Location" locations if there are no shifts and they are outside of config!!!
											return null;
										}

										const rowKey = [
											section || "NO_SECTION",
											currentLocation.id ?? `_no_id_${uuidv4()}`,
											currentLocation.location || "NOLOC",
											currentLocation.shift_start?.slice(0, 5) ?? "00:00",
											currentLocation.shift_end?.slice(0, 5) ?? "00:00",
											(currentLocation.roles ?? []).join("_") || "noroles",
											scheduleData.isLocationOutsideOfConfig ? "temp" : "cfg",
										].join("|");

										return (
											<MasterScheduleRow
												key={rowKey}
												rowKey={rowKey}
												currentLocation={currentLocation}
												isCollapsed={isCollapsed}
												amountOfWeeks={amountOfWeeks}
												activeDragId={activeDragId}
												departmentSpecificRoles={departmentSpecificRoles}
												refetchRecurringShifts={refetchRecurringShifts}
												handleOnAddClick={handleOnAddClick}
												uniqueSections={uniqueSections}
												onShiftClick={onShiftClick}
												onShiftKeyDown={onShiftKeyDown}
												scheduleData={scheduleData}
												currentWeeklyShifts={currentWeeklyShifts}
												onCtrlPressedChange={handleOnCtrlPressedChange}
												isSynergyMS={isSynergyMS}
												isDraggingSection={isDraggingSection}
												getNurseById={getNurseById}
												isHidden={!showAll && scheduleIndex >= DEFAULT_VISIBLE_LOCATIONS}
												isFadedOut={!showAll && scheduleIndex + 1 === DEFAULT_VISIBLE_LOCATIONS && totalLocations > DEFAULT_VISIBLE_LOCATIONS}
												isLastRow={scheduleIndex === visibleScheduleData.length - 1}
											/>
										);
									})}
									{!isCollapsed && totalLocations > DEFAULT_VISIBLE_LOCATIONS && (
										<Box sx={showAllFooterStyle(!showAll)} onClick={() => toggleShowAllLocations(section)}>
											<Typography component="span" className={showAllBtnClassName} sx={showAllBtnStyle}>
												{showAll ? (
													<>
														{t("masterSchedule.showLess")}
														<KeyboardArrowUp sx={{ fontSize: "16px" }} />
													</>
												) : (
													<>
														{t("masterSchedule.showAll", { count: totalLocations })}
														<KeyboardArrowDown sx={{ fontSize: "16px" }} />
													</>
												)}
											</Typography>
										</Box>
									)}
								</Box>
							</Fragment>
						);
					})
				)}
				{calculatedData && <SectionDropZone index={orderedSections.length} amountOfWeeks={amountOfWeeks} isVisible={isDraggingSection} />}
			</Box>
		</LocationCellProvider>
	);
};
